import type { LangCode } from "@/lib/types";

/**
 * The locales this site serves. One source, imported everywhere.
 *
 * This list used to be copy-pasted into thirteen places across eight files —
 * router, I18nContext, the nitro entry three times, seo.ts three times,
 * shop-slug, __root and the sitemap — with nothing comparing them. Adding an
 * eleventh locale meant finding all thirteen, and missing one fails silently
 * in a way no page visibly breaks on: the locale renders but is absent from
 * hreflang, or its prefix 404s, or it never reaches the sitemap.
 *
 * Order is not meaningful here; this is a membership test. For the order that
 * IS meaningful, see ALTERNATE_LANGS.
 */
export const VALID_LANGS: LangCode[] = [
  "tr",
  "ar",
  "de",
  "ru",
  "ja",
  "ko",
  "en",
  "zh",
  "es",
  "fa",
  "fr",
];

/**
 * The same locales, in the order they are PUBLISHED.
 *
 * Separate from VALID_LANGS because this order reaches the user: it is the
 * sequence of `hreflang` links in every document head and of `xhtml:link`
 * entries in the sitemap. English leads because it is the unprefixed default.
 * Kept as a distinct constant rather than sorting VALID_LANGS so that a
 * refactor of one cannot silently reorder the other's output.
 *
 * `locale.test.ts` asserts the two stay the same set.
 */
export const ALTERNATE_LANGS = [
  "en",
  "tr",
  "ar",
  "de",
  "ru",
  "ja",
  "ko",
  "zh",
  "es",
  "fa",
  "fr",
] as const;

/** Isomorphic locale resolution — mirrors the pattern in seo.ts's getSeoMeta(). */
export function getCurrentLocale(): LangCode {
  if (typeof window !== "undefined") {
    const first = window.location.pathname.split("/").filter(Boolean)[0]?.toLowerCase();
    if (first && VALID_LANGS.includes(first as LangCode)) {
      return first as LangCode;
    }
    return "en";
  }
  const storage = (globalThis as unknown as Record<string, unknown>).serverStorage as
    { getStore: () => { lang?: string } | undefined } | undefined;
  const lang = storage?.getStore()?.lang;
  return lang && VALID_LANGS.includes(lang as LangCode) ? (lang as LangCode) : "en";
}
